import type { LucideIcon } from 'lucide-react';

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  color?: 'blue' | 'green' | 'orange' | 'red' | 'purple' | 'amber';
  trend?: {
    value: number;
    direction: 'up' | 'down';
    label?: string;
  };
  subtitle?: string;
  onClick?: () => void;
}

const colorClasses = {
  blue: {
    bg: 'bg-blue-50 dark:bg-blue-950/30',
    icon: 'bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-400',
    value: 'text-blue-600 dark:text-blue-400',
  },
  green: {
    bg: 'bg-emerald-50 dark:bg-emerald-950/30',
    icon: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/50 dark:text-emerald-400',
    value: 'text-emerald-600 dark:text-emerald-400',
  },
  orange: {
    bg: 'bg-orange-50 dark:bg-orange-950/30',
    icon: 'bg-orange-100 text-orange-600 dark:bg-orange-900/50 dark:text-orange-400',
    value: 'text-orange-600 dark:text-orange-400',
  },
  red: {
    bg: 'bg-rose-50 dark:bg-rose-950/30',
    icon: 'bg-rose-100 text-rose-600 dark:bg-rose-900/50 dark:text-rose-400',
    value: 'text-rose-600 dark:text-rose-400',
  },
  purple: {
    bg: 'bg-violet-50 dark:bg-violet-950/30',
    icon: 'bg-violet-100 text-violet-600 dark:bg-violet-900/50 dark:text-violet-400',
    value: 'text-violet-600 dark:text-violet-400',
  },
  amber: {
    bg: 'bg-amber-50 dark:bg-amber-950/30',
    icon: 'bg-amber-100 text-amber-600 dark:bg-amber-900/50 dark:text-amber-400',
    value: 'text-amber-600 dark:text-amber-400',
  },
};

export default function MetricCard({
  title,
  value,
  icon: Icon,
  color = 'blue',
  trend,
  subtitle,
  onClick,
}: MetricCardProps) {
  const colors = colorClasses[color];

  return (
    <div
      className={`rounded-xl border ${colors.bg} p-4 transition-all ${
        onClick ? 'cursor-pointer hover:shadow-md hover:border-primary/30' : ''
      }`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {title}
          </p>
          <p className={`text-2xl font-bold mt-1 ${colors.value}`}>{value}</p>
          {subtitle && (
            <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
          )}
          {trend && (
            <p
              className={`text-xs mt-1 ${
                trend.direction === 'up' ? 'text-emerald-600' : 'text-rose-600'
              }`}
            >
              {trend.direction === 'up' ? '↑' : '↓'} {trend.value}%{' '}
              {trend.label && <span className="text-muted-foreground">{trend.label}</span>}
            </p>
          )}
        </div>
        <div className={`p-2.5 rounded-lg ${colors.icon}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}
